'use client';

import { IconCheck, IconSend } from '@tabler/icons-react';
import { motion } from 'framer-motion';
import { useParams } from 'next/navigation';
import Link from 'next/link';
import React, { useState } from 'react';
import { Button } from '@/components/elements/button';
import { StrapiImage } from '@/components/ui/strapi-image';
import { useCart } from '@/context/cart-context';
import { strapiImage } from '@/lib/strapi/strapiImage';
import { jakarta } from '@/lib/fonts';
import { cn, formatNumber } from '@/lib/utils';
import { Product } from '@/types/types';
import { TrainingPresentationGrid } from './training-presentation-grid';
import { TrainingHero } from './training-hero';
import { tagColorMap } from './product-items';

export const SingleProduct = ({
  product,
  isService = false,
  isTraining = false,
}: {
  product: Product;
  isService?: boolean;
  isTraining?: boolean;
}) => {
  const params = useParams();
  const locale = (params?.locale as string) || 'en';

  if (isTraining || isService) {
    return (
      <div className="flex flex-col gap-6 sm:gap-8">
        <TrainingHero
          product={product}
          locale={locale}
          isService={isService}
          isTraining={isTraining}
        />
        {product.perks && product.perks.length > 0 && (
          <TrainingPresentationGrid
            perks={product.perks}
            locale={locale}
            isService={isService}
          />
        )}
      </div>
    );
  }

  const [activeThumbnail, setActiveThumbnail] = useState(
    product.images && product.images[0] ? strapiImage(product.images[0].url) : ''
  );
  const { addToCart } = useCart();

  return (
    <div className="bg-white dark:bg-neutral-900 p-6 md:p-12 rounded-3xl border border-[#e5e2f5] dark:border-neutral-800 shadow-xl shadow-[#301754]/5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-12 lg:gap-20">
        <div>
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            key={activeThumbnail}
            className="rounded-2xl relative overflow-hidden shadow-xl shadow-neutral-200/50 dark:shadow-none"
            transition={{
              type: 'spring',
              stiffness: 260,
              damping: 35,
            }}
          >
            <StrapiImage
              src={activeThumbnail}
              alt={product.name}
              width={800}
              height={800}
              className="rounded-2xl object-cover w-full aspect-square"
            />
          </motion.div>
          
          {product.images && product.images.length > 1 && (
            <div className="flex gap-4 justify-center items-center mt-6 flex-wrap">
              {product.images.map((image, index) => (
                <button
                  onClick={() => setActiveThumbnail(strapiImage(image.url))}
                  key={'product-image' + index}
                  className={cn(
                    'h-20 w-20 rounded-xl overflow-hidden transition-all duration-200 relative',
                    activeThumbnail === strapiImage(image.url)
                      ? 'ring-2 ring-primary ring-offset-2 dark:ring-offset-neutral-900 scale-105'
                      : 'ring-1 ring-neutral-200 dark:ring-neutral-800 hover:opacity-80'
                  )}
                  style={{
                    backgroundImage: `url(${strapiImage(image.url)})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                  }}
                />
              ))}
            </div>
          )}
        </div>

        <div className="flex flex-col justify-center">
          {product.tags && product.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {product.tags.map((tag) => (
                <span
                  key={tag.id}
                  className={cn(
                    'px-3 py-1 text-sm font-semibold rounded-full border transition-colors duration-200',
                    tagColorMap[tag.color] || tagColorMap.blue
                  )}
                >
                  {tag.text}
                </span>
              ))}
            </div>
          )}
          <h2
            className={cn(
              jakarta.className,
              'text-3xl md:text-5xl font-bold mb-4 text-[#2e1452] dark:text-white tracking-tight'
            )}
          >
            {product.name}
          </h2>
          {product.show_price !== false && (
            <div className="mb-8">
              <span className="inline-flex items-center justify-center bg-white dark:bg-neutral-800 border border-[#e5e2f5] dark:border-neutral-700 text-[#2e1452] dark:text-white text-lg font-bold px-6 py-2 rounded-full shadow-sm">
                {formatNumber(product.price)} €
              </span>
            </div>
          )}
          
          <p className="text-base sm:text-lg font-medium leading-relaxed mb-8 text-[#5e5873] dark:text-neutral-300">
            {product.description}
          </p>

          {(isService || isTraining) && (
            <div className="mb-8">
              <Button
                as={Link}
                href={`/${locale}/contact?${isTraining ? 'training' : 'service'}=${encodeURIComponent(product.name)}`}
                variant="primary"
                className="w-full sm:w-auto px-8 py-3 text-base font-semibold flex items-center justify-center gap-2"
              >
                <span>
                  {isTraining
                    ? (locale === 'fr' ? 'S’inscrire à la formation' : 'Enroll / Inquire')
                    : (locale === 'fr' ? 'Demander un devis' : 'Request Quote')}
                </span>
                <IconSend className="h-4 w-4" />
              </Button>
            </div>
          )}

          {!isTraining && (
            <>
              <Divider />
              <ul className="flex flex-col gap-2 mb-10 mt-8">
                {product.perks &&
                  product.perks.map((perk, index) => (
                    <Step key={index}>{perk.text}</Step>
                  ))}
              </ul>
            </>
          )}
          
          {product.plans && product.plans.length > 0 && (
            <>
              <h3 className="text-sm font-bold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-4">
                Available for
              </h3>
              <ul className="list-none flex gap-3 flex-wrap mb-10">
                {product.plans.map((plan, index) => (
                  <li
                    key={index}
                    className="bg-neutral-200/50 dark:bg-neutral-800 text-sm text-neutral-800 dark:text-neutral-200 px-4 py-2 rounded-full font-semibold border border-neutral-200 dark:border-neutral-700"
                  >
                    {plan.name}
                  </li>
                ))}
              </ul>
            </>
          )}

          {product.categories && product.categories.length > 0 && (
            <>
              <h3 className="text-sm font-bold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-4">
                Categories
              </h3>
              <ul className="flex gap-3 flex-wrap">
                {product.categories.map((category, idx) => (
                  <li
                    key={`category-${idx}`}
                    className="bg-neutral-100 dark:bg-neutral-800/50 text-sm text-neutral-600 dark:text-neutral-400 px-4 py-2 rounded-full font-medium"
                  >
                    {category.name}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </div>

      {isTraining && product.perks && product.perks.length > 0 && (
        <TrainingPresentationGrid perks={product.perks} locale={locale} />
      )}
    </div>
  );
};

const Divider = () => {
  return (
    <div className="w-full h-px bg-neutral-200 dark:bg-neutral-800 my-2" />
  );
};

const Step = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="flex items-start justify-start gap-3 my-0">
      <div className="h-6 w-6 rounded-full bg-primary/10 dark:bg-primary/20 flex items-center justify-center flex-shrink-0">
        <IconCheck className="h-4 w-4 [stroke-width:3px] text-primary" />
      </div>
      <div className="font-medium text-neutral-700 dark:text-neutral-300 text-base leading-relaxed">{children}</div>
    </div>
  );
};
